/**
 * Membership inside the donor's profile sheet (GH-864e, plan §5 "Donor web").
 *
 * The web shows membership state and invites; **influence stays app-first** — a member reads one
 * line pointing at the app, and no figure is drawn here. Everything in this file is hidden while
 * `FEATURE_MEMBERSHIP` is off: the membership read answers 404, the hook turns that into `null`,
 * and every component below renders nothing for `null`.
 *
 * Scarcity ("3 invites left", "#1,214 in line") is DEC-051's exception for membership surfaces;
 * none of it reaches a give, checkout or embed surface. Invites never depend on the donor's own
 * giving, and nothing here mentions giving as a way to earn them.
 *
 * The platform never messages anybody: an invite is a link the donor shares through their own
 * share sheet, or copies.
 */
import { useRef, useState } from "react";
import {
  MEMBERSHIP_COPY,
  type DonorMembershipResponse,
  type IssuedInviteResponse,
  type MemberInviteWire,
} from "@givehere/contracts";
import {
  DonorApiError,
  fetchDonorMembership,
  issueInvite,
  joinWaitlist,
  recordShare,
  redeemInvite,
  reissueInvite,
  revokeInvite,
} from "./donor-session.js";
import { Explain } from "./Explain.js";
import { InviteCodeForm, redeemFailure } from "./InviteCodeForm.js";
import { grouped, shareOrCopy, type ShareOutcome } from "./share.js";

export const SEE_INFLUENCE_IN_APP = "See your influence in the app";

const failure = (caught: unknown): string =>
  caught instanceof DonorApiError ? caught.message : "That didn’t work. Try again.";

export const invitesLeftLine = (available: number): string =>
  available === 1
    ? MEMBERSHIP_COPY.invitesLeftOne
    : MEMBERSHIP_COPY.invitesLeft(grouped(available));

/* ------------------------------------------------------------------ the profile's summary */

/** The block on the profile's first screen. Renders nothing until the gate and state are known. */
export function MembershipSummary({
  token,
  membership,
  onChange,
  onInvites,
  onHaveInvite,
}: {
  token: string;
  membership: DonorMembershipResponse | null | undefined;
  onChange: (next: DonorMembershipResponse) => void;
  onInvites: () => void;
  onHaveInvite: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!membership) return null;

  const member = membership.member;
  return (
    <section className="membership" aria-label="Membership">
      {membership.state === "member" && member ? (
        <>
          <p className="membership__line">
            {MEMBERSHIP_COPY.memberNumber(grouped(member.number))}
            {member.founding && (
              <span className="membership__keyline"> · {MEMBERSHIP_COPY.foundingMember}</span>
            )}
          </p>
          {/* A platform admin's account issues without an allotment (GH-871a): no count at all. */}
          {membership.invites && !membership.invites.unlimited && (
            <p className="membership__line membership__line--muted">
              {invitesLeftLine(membership.invites.available)}
            </p>
          )}
          <div className="donor__actions">
            <button type="button" className="link" onClick={onInvites}>
              {MEMBERSHIP_COPY.invitesTitle}
            </button>
          </div>
          <p className="membership__line membership__line--muted">{SEE_INFLUENCE_IN_APP}</p>
        </>
      ) : (
        <>
          {membership.state === "waiting" && membership.waitlist ? (
            <p className="membership__line">
              {MEMBERSHIP_COPY.waitlistPosition(grouped(membership.waitlist.position))}
            </p>
          ) : null}
          <div className="donor__actions">
            {membership.state === "none" && (
              <button
                type="button"
                className="link"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setBusy(true);
                  joinWaitlist(token)
                    .then(onChange)
                    .catch((caught: unknown) => setError(failure(caught)))
                    .finally(() => setBusy(false));
                }}
              >
                {MEMBERSHIP_COPY.joinWaitlist}
              </button>
            )}
            <button type="button" className="link" onClick={onHaveInvite}>
              {MEMBERSHIP_COPY.haveInvite}
            </button>
          </div>
        </>
      )}
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ have an invite */

export function HaveInviteStep({
  token,
  onChange,
  onBack,
}: {
  token: string;
  onChange: (next: DonorMembershipResponse) => void;
  onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState<DonorMembershipResponse | null>(null);
  return (
    <>
      <h2 id="donor-title" className="donor__title">
        {joined?.member
          ? MEMBERSHIP_COPY.welcomeMember(grouped(joined.member.number))
          : MEMBERSHIP_COPY.haveInvite}
      </h2>
      {!joined && (
        <InviteCodeForm
          busy={busy}
          onSubmit={(code) => {
            setError(null);
            setBusy(true);
            redeemInvite(token, code)
              .then((next) => {
                setJoined(next);
                onChange(next);
              })
              .catch((caught: unknown) => setError(redeemFailure(caught)))
              .finally(() => setBusy(false));
          }}
        />
      )}
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <div className="donor__actions">
        <button type="button" className="link" onClick={onBack}>
          Back
        </button>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------------- invites */

const STATUS_LABEL: Record<Exclude<MemberInviteWire["status"], "open">, string> = {
  redeemed: MEMBERSHIP_COPY.inviteRedeemed,
  revoked: MEMBERSHIP_COPY.inviteRevoked,
  expired: MEMBERSHIP_COPY.inviteExpired,
};

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function InvitesStep({
  token,
  membership,
  onChange,
  onBack,
}: {
  token: string;
  membership: DonorMembershipResponse;
  onChange: (next: DonorMembershipResponse) => void;
  onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The invite just issued: its code exists only in this answer, so it is shared from here. */
  const [ready, setReady] = useState<IssuedInviteResponse | null>(null);
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null);
  /** One key per attempt, kept across a retry so a double tap issues one invite, not two. */
  const attemptKey = useRef<string | null>(null);

  const member = membership.member;
  const invites = membership.invites;
  if (!member || !invites) return null;
  const paused = member.invitesFrozen || member.status === "suspended";
  // GH-864h TS-L5: the new invite's link is shown only while that invite can still be used — a
  // revoke (here, or by a freeze) takes it off the screen rather than leaving a dead link to share.
  const readyStatus = ready
    ? invites.items.find((item) => item.id === ready.inviteId)?.status
    : null;
  const shownReady = ready && (readyStatus === undefined || readyStatus === "open") ? ready : null;

  const refresh = () =>
    fetchDonorMembership(token).then((next) => {
      if (next) onChange(next);
    });

  const run = (work: () => Promise<IssuedInviteResponse | null>) => {
    setError(null);
    setOutcome(null);
    setBusy(true);
    work()
      .then(async (issued) => {
        if (issued) setReady(issued);
        await refresh();
      })
      .catch((caught: unknown) => setError(failure(caught)))
      .finally(() => setBusy(false));
  };

  const share = async (invite: IssuedInviteResponse, how: "sheet" | "copy") => {
    const result =
      how === "sheet"
        ? await shareOrCopy({ url: invite.url, text: invite.messages[0]?.text })
        : await navigator.clipboard
            ?.writeText(invite.url)
            .then((): ShareOutcome => "copied")
            .catch((): ShareOutcome => "failed");
    setOutcome(result ?? "failed");
    if (result === "shared" || result === "copied") {
      void recordShare(token, "invite").catch(() => undefined);
    }
  };

  return (
    <>
      <h2 id="donor-title" className="donor__title">
        {MEMBERSHIP_COPY.invitesTitle}
      </h2>
      {/* GH-871b: `unlimited` (a platform admin's account) shows no count, and never runs out. */}
      {!invites.unlimited && <p className="donor__where">{invitesLeftLine(invites.available)}</p>}
      <Explain
        className="donor__where"
        label={MEMBERSHIP_COPY.invitesExplanationTitle}
        title={MEMBERSHIP_COPY.invitesExplanationTitle}
      >
        <p>{MEMBERSHIP_COPY.invitesExplanation}</p>
      </Explain>

      {paused ? (
        <p className="notice">{MEMBERSHIP_COPY.invitesPaused}</p>
      ) : (
        <button
          type="button"
          className="pay"
          disabled={busy || (!invites.unlimited && invites.available === 0)}
          onClick={() => {
            attemptKey.current ??= globalThis.crypto.randomUUID();
            const key = attemptKey.current;
            run(async () => {
              const issued = await issueInvite(token, key);
              attemptKey.current = null;
              return issued;
            });
          }}
        >
          {busy ? "Working…" : MEMBERSHIP_COPY.newInvite}
        </button>
      )}

      {shownReady && (
        <section className="invite-ready" aria-label="Your new invite">
          <p className="invite-ready__url" translate="no">
            {shownReady.url}
          </p>
          <div className="invite-ready__actions">
            <button
              type="button"
              className="pay pay--outline"
              onClick={() => void share(shownReady, "sheet")}
            >
              {MEMBERSHIP_COPY.shareInvite}
            </button>
            <button type="button" className="link" onClick={() => void share(shownReady, "copy")}>
              {MEMBERSHIP_COPY.copyLink}
            </button>
          </div>
          <span className="muted small" role="status">
            {outcome === "copied"
              ? MEMBERSHIP_COPY.linkCopied
              : outcome === "failed"
                ? "Couldn’t share the link"
                : ""}
          </span>
        </section>
      )}

      {invites.items.length > 0 && (
        <ul className="invite-list">
          {invites.items.map((invite) => (
            <li key={invite.id} className="invite-list__row">
              <span className="invite-list__what">
                {MEMBERSHIP_COPY.inviteSent(shortDate(invite.createdAt))}
                {invite.status === "open" &&
                  ` · ${MEMBERSHIP_COPY.inviteExpires(shortDate(invite.expiresAt))}`}
                {invite.status !== "open" && (
                  <span className="invite-list__status"> · {STATUS_LABEL[invite.status]}</span>
                )}
              </span>
              {invite.status === "open" && !paused && (
                <span className="invite-list__actions">
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() => run(() => reissueInvite(token, invite.id))}
                  >
                    {MEMBERSHIP_COPY.shareAgain}
                  </button>
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await revokeInvite(token, invite.id);
                        return null;
                      })
                    }
                  >
                    {MEMBERSHIP_COPY.revokeInvite}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <div className="donor__actions">
        <button type="button" className="link" onClick={onBack}>
          Back
        </button>
      </div>
    </>
  );
}
